//frontend/src/api/status.ts
import { api } from "./client";

export interface StatusOverview {
  total_leaks: number;
  hidden_leaks: number;
  visible_leaks: number;
  total_status_records: number;
  failed_jobs: number;
  retry_jobs: number;
  leaks_per_forum: Record<string, number>;
  leaks_per_sector: Record<string, number>;
  leaks_per_data_type?: Record<string, number>;
  activity_7d?: number[];
  active_takedowns?: number;
}
export interface ForumStat {
  forum: string;
  count: number;
}

export interface WhatsNew {
  new_last_24h: number;
  new_last_7d: number;
  hidden_last_7d: number;
  top_forums_last_7d: {
    forum: string;
    count: number;
  }[];
}

export interface ForumRisk {
  forum: string;
  leaks_last_7d: number;
  growth_pct: number | null;
  hidden_pct: number;
}

export const fetchStatusOverview = async (): Promise<StatusOverview> => {
  const res = await api.get("/status/");
  return res.data;
};

export const fetchForums = async (): Promise<ForumStat[]> => {
  const res = await api.get("/status/forums");
  return res.data;
};

export const fetchWhatsNew = async (): Promise<WhatsNew> => {
  const res = await api.get("/status/whats-new");
  return res.data;
};

export interface TimelineEntry {
  date: string;
  count: number;
  forums: string[];
}

export const fetchForumRisk = async (): Promise<ForumRisk[]> => {
  const res = await api.get("/status/forum-risk");
  return res.data;
};

export const fetchTimeline = async (params?: {
  days?: number;
  forum?: string;
  sector?: string;
}): Promise<TimelineEntry[]> => {
  const res = await api.get("/leaks/timeline", { params });
  const rawList = Array.isArray(res.data) ? res.data : (res.data?.timeline || []);
  return rawList.map((item: any) => ({
    date: item.date,
    count: typeof item.count === "number" ? item.count : 0,
    forums: Array.isArray(item.forums)
      ? item.forums
      : Array.isArray(item.leaks)
      ? [...new Set(item.leaks.map((l: any) => l.forum).filter(Boolean))]
      : [],
  }));
};

export const triggerUrlscanScraper = async (): Promise<{ status: string; message: string }> => {
  const res = await api.post("/admin/jobs/run-urlscan");
  return res.data;
};

export interface ScraperForumStatus {
  forum: string;
  last_check: string | null;
  status: string;
  urls: number | null;
  new: number | null;
  errors: number | null;
  leaks_24h: number | null;
}

export interface ScraperStatusResponse {
  summary: {
    total: number;
    status_counts: Record<string, number>;
  };
  forums: ScraperForumStatus[];
  updated_at: string | null;
}

export const fetchScraperForumStatus = async (): Promise<ScraperStatusResponse> => {
  const res = await api.get("/admin/scraper-status");
  return res.data;
};

export interface NationsResponse {
  total: number;
  timeframe?: string;
  nations: {
    country: string;
    code: string;
    flag: string;
    count: number;
    percentage: number;
    color: string;
    cx?: number;
    cy?: number;
    top_actors?: string[];
  }[];
}

import { normalizeCountry } from "../utils/country";

export const fetchNations = async (params?: {
  timeframe?: string;
  days?: number;
}): Promise<NationsResponse> => {
  console.log("GET /status/nations - Request Params:", params);
  const res = await api.get("/status/nations", { params });
  console.log("GET /status/nations - Raw Axios Response:", res);

  const rawData = res.data;
  let rawList: any[] = [];
  let total = 0;
  let tf = params?.timeframe || "30d";

  if (Array.isArray(rawData)) {
    rawList = rawData;
  } else if (rawData && typeof rawData === "object") {
    total = typeof rawData.total === "number" ? rawData.total : 0;
    tf = rawData.timeframe || tf;
    if (Array.isArray(rawData.nations)) {
      rawList = rawData.nations;
    } else if (Array.isArray(rawData.data)) {
      rawList = rawData.data;
    } else if (rawData.data && Array.isArray(rawData.data.nations)) {
      rawList = rawData.data.nations;
    }
  }

  const grandTotal = total > 0 ? total : rawList.reduce((acc, item) => acc + (Number(item.count) || 0), 0);

  const normalizedList = rawList.map((item: any) => {
    const rawCountry = item.country || item.name || item.norm_country || "";
    const rawCode = item.code || item.iso2 || "";
    const norm = normalizeCountry(rawCountry, rawCode);
    const cnt = typeof item.count === "number" ? item.count : Number(item.count) || 0;
    const pct = typeof item.percentage === "number"
      ? item.percentage
      : grandTotal > 0
      ? Math.round((cnt / grandTotal) * 100)
      : 0;

    return {
      country: norm.name,
      code: norm.iso2,
      flag: item.flag && item.flag !== "🌐" ? item.flag : norm.flag,
      count: cnt,
      percentage: pct,
      color: item.color || "#ef4444",
      cx: item.cx,
      cy: item.cy,
      top_actors: Array.isArray(item.top_actors) ? item.top_actors : [],
    };
  });

  normalizedList.sort((a, b) => b.count - a.count);

  console.log("GET /status/nations - Final Parsed Nations:", normalizedList);

  return {
    total: grandTotal,
    timeframe: tf,
    nations: normalizedList,
  };
};

export const fetchNationAttacks = async (country: string, timeframe?: string): Promise<{ country: string; attacks: any[] }> => {
  const res = await api.get("/status/nations/attacks", { params: { country, timeframe } });
  return res.data;
};

export interface RansomwareDailyPoint {
  date: string;
  label: string;
  dayOfWeek: string;
  fullDate: string;
  count: number;
}

export interface RansomwareActor {
  rank: number;
  name: string;
  attacks: number;
  share_pct: number;
  sectors: string[];
}

export interface RansomwareSector {
  name: string;
  count: number;
  percentage: number;
  heatmap: number[];
  active_actors: string[];
}

export interface RansomwareSummary {
  attacks: number;
  actors: number;
  topSector: string;
  topSectorPct: number;
  topSectorCount: number;
  peakDay?: {
    date: string;
    label: string;
    fullDate: string;
    count: number;
  } | null;
}

export interface RansomwareResponse {
  timeframe: string;
  summary: RansomwareSummary;
  daily: RansomwareDailyPoint[];
  actors: RansomwareActor[];
  sectors: RansomwareSector[];
}


export interface RansomwareVictim {
  id: string;
  victim: string;
  sector: string;
  country: string;
  flag: string;
  date: string;
  forum: string;
}

export interface RansomwareGroupDetails {
  group_name: string;
  total_attacks: number;
  timeframe: string;
  targeted_sectors: {
    name: string;
    count: number;
    percentage: number;
  }[];
  recent_victims: RansomwareVictim[];
  daily_timeline: {
    date: string;
    label: string;
    count: number;
  }[];
}

export const fetchRansomwareAnalytics = async (params?: {
  timeframe?: string;
}): Promise<RansomwareResponse> => {
  const res = await api.get("/status/ransomware", { params });
  return res.data;
};

export const fetchRansomwareGroupDetails = async (
  groupName: string,
  timeframe?: string
): Promise<RansomwareGroupDetails> => {
  const res = await api.get(`/status/ransomware/group/${encodeURIComponent(groupName)}`, {
    params: { timeframe },
  });
  return res.data;
};



